<script setup>
    import { useElementVisibility, useUrlSearchParams, watchDebounced } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, defineAsyncComponent, inject, nextTick, onMounted, ref, unref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import useLiferayLocale from '../../composables/useLiferayLocale';
    import axios from '../../setup/axios';
    import escapeQuery from '../../helpers/escapeQuery';
    import { useDebug } from '../../composables/useDebug';
    import { SEARCH } from '../Search/config';
    import { useSearchStore } from '../Search/store/useSearchStore';
    import { register } from 'swiper/element/bundle';
    import NewsTeaserCard from './components/NewsTeaserCard';
    register();

    const API_ENDPOINT = '/o/ams-osram-search/news/search';
    const PressLinks = defineAsyncComponent(() => import('./components/PressLinks.vue'));
    const { log, error } = useDebug();
    const { cldrLocale } = useLiferayLocale();
    const { t } = useI18n();
    const $bus = inject('$bus');
    const params = useUrlSearchParams('history');

    const newsTypFilterStartRef = ref(null);
    const newsTypFilterEndRef = ref(null);
    const isNewsTypFilterStartVisible = useElementVisibility(newsTypFilterStartRef);
    const isNewsTypFilterEndVisible = useElementVisibility(newsTypFilterEndRef);

    const newsCategoryFilterStartRef = ref(null);
    const newsCategoryFilterEndRef = ref(null);
    const isNewsCategoryFilterStartVisible = useElementVisibility(newsCategoryFilterStartRef);
    const isNewsCategoryFilterEndVisible = useElementVisibility(newsCategoryFilterEndRef);

    const newsYearsFilterStartRef = ref(null);
    const newsYearsFilterEndRef = ref(null);
    const isNewsYearsFilterStartVisible = useElementVisibility(newsYearsFilterStartRef);
    const isNewsYearsFilterEndVisible = useElementVisibility(newsYearsFilterEndRef);

    const store = useSearchStore();
    store.currentPage = 0;

    // Evaluate query parameters on initial load
    onMounted(() => {
        let newsTypes = [];
        if (Object.hasOwn(params, 'news')) {
            newsTypes = params.news.split(',');
        } else if (props.newsTypesFilter.length > 0) {
            newsTypes = props.newsTypesFilter.map(filter => filter.key);
        }

        let newsCategories = [];
        if (Object.hasOwn(params, 'news-hub')) {
            newsCategories = params['news-hub'].split(',');
        } else if (props.newsHubFilter.length > 0) {
            newsCategories = props.newsHubFilter.map(filter => filter.key);
        }

        let newsApplications = [];
        if (Object.hasOwn(params, 'applications')) {
            newsApplications = params.applications.split(',');
        } else if (props.newsApplicationsFilter.length > 0) {
            newsApplications = props.newsApplicationsFilter.map(filter => filter.key);
        }

        store.$patch({
            selectedNewsTypes: newsTypes,
            selectedNewsCategories: newsCategories,
            selectedNewsApplications: newsApplications,
            selectedNewsYear: Object.hasOwn(params, 'year')
                ? parseInt(params.year)
                : Object.hasOwn(params, 'archive')
                  ? SEARCH.NEWS_YEAR_ARCHIVE
                  : null,
        });
    });

    const props = defineProps({
        newsTypes: {
            type: Array,
            required: true,
        },

        newsTypesFilter: {
            type: Array,
            required: false,
            default: [],
        },

        categories: {
            type: Array,
            required: true,
        },

        newsHubFilter: {
            type: Array,
            required: false,
            default: [],
        },

        applications: {
            type: Array,
            required: true,
        },

        newsApplicationsFilter: {
            type: Array,
            required: false,
            default: [],
        },

        showPressLinks: {
            type: Boolean,
            default: false,
        },

        contactPersonLabel: {
            type: String,
            default: 'Contact person',
        },

        contactPersonUrl: {
            type: String,
            default: null,
        },

        multimediaLabel: {
            type: String,
            default: 'Multimedia',
        },

        multimediaUrl: {
            type: String,
            default: null,
        },

        newsletterLabel: {
            type: String,
            default: 'Newsletter',
        },

        newsletterUrl: {
            type: String,
            default: null,
        },

        fallbackImage: {
            type: String,
            default:
                'https://look.ams-osram.com/transform/98089e6c-3697-4e87-b85a-a3e42ad9899d/ams-OSRAM-Texture-Assets?format=webp&io=transform:fill,width:1280',
        },

        showSearchBar: {
            type: Boolean,
            default: false,
        },

        showTypeFilter: {
            type: Boolean,
            default: false,
        },

        showCategoryFilter: {
            type: Boolean,
            default: false,
        },

        showYearFilter: {
            type: Boolean,
            default: false,
        },

        showNewsHeader: {
            type: Boolean,
            default: false,
        },

        showFeaturedNews: {
            type: Boolean,
            default: false,
        },

        showHighlightTitle: {
            type: Boolean,
            default: false,
        },

        showNewsTeaser: {
            type: Boolean,
            default: false,
        },

        newsTeaserTitle: {
            type: String,
            default: null,
        },

        newsTeaserAbstract: {
            type: String,
            default: null,
        },

        newsTeaserLinkHref: {
            type: String,
            default: null,
        },

        newsTeaserLinkText: {
            type: String,
            default: null,
        },

        newsTeaserBackgroundImage: {
            type: String,
            default: null,
        },
        pressArea: {
            type: Boolean,
            default: false,
        },
        pressAreaFilterElements: {
            type: Array,
            required: false,
            default: [],
        },
        stopLoading: {
            type: Boolean,
            required: false,
            default: false,
        },
        sizeOfElements: {
            type: String,
            required: false,
            default: '25',
        },
        enableRotate: {
            type: Boolean,
            required: false,
            default: false,
        },
        autoPlayDelay: {
            type: String,
            required: false,
            default: '',
        },
    });

    const currentYear = new Date().getFullYear();
    const years = computed(() => {
        const endYear = currentYear - 4;
        const step = -1;

        return Array.from({ length: (endYear - currentYear) / step + 1 }, (_, i) => currentYear + i * step);
    });

    /** @type {AbortController | null} */
    const abortController = ref(null);

    /**
     * Reset the cancel callback and busy states.
     *
     * @returns {void}
     */
    const resetRequest = () => {
        abortController.value = null;
    };

    const handleResult = response => {
        log?.(response, response.request.currentPage);

        const startingIndex = Object.hasOwn(response, 'startIndex') ? response.startIndex : 0;
        const featuredResults = response.featured || [];
        const allResults = response.results;

        store.$patch({
            results: response.request.currentPage === 1 ? allResults : [].concat(store.results, allResults),
            featuredResults:
                response.request.currentPage === 1
                    ? featuredResults
                    : [].concat(store.featuredResults, featuredResults),
            totalResults: Object.hasOwn(response, 'totalCount') ? response.totalCount : response.results.length,
            currentPage: Math.ceil(startingIndex / SEARCH.RESULTS_PER_PAGE) + 1,
            facets: response.aggregations,
            busy: false,
        });

        const currentCount = store.results.length + store.featuredResults.length;
        if (!response.results || !response.results.length || currentCount >= response.totalCount) {
            store.stopObserving();
        }

        nextTick(() => {
            store.updateNewsUrlParameters();
            if (swiperRef.value && swiperRef.value.swiper) {
                swiperRef.value.swiper.update();
                handleSwiperInit(swiperRef.value.swiper);
            }
        });
    };

    /**
     * Handle a search request.
     *
     * @param {null | string} customSearchQuery The search query, defaults to the store value.
     * @param {number} page The page number to go to.
     * @returns {void}
     */
    const handleSearch = (customSearchQuery = null, page = 1) => {
        if (store.busy) {
            return;
        }

        const query = (customSearchQuery || store.searchQuery)?.trim() || '';

        if (query.length === 0) {
            store.searchQuery = null;
        }

        log?.('🔎️ searching for: ', query, page);

        // Set status to busy (shows spinner)
        store.busy = true;

        // Cancel previous request
        if (abortController.value) {
            abortController.value.abort();
        }
        abortController.value = new AbortController();

        // This has a side effect that we cant disable all filters if we have preselected ones, also I think this should be done in onMounted function
        //
        // if(store.selectedNewsTypes.length === 0 && props.newsTypesFilter.length > 0) {
        //     props.newsTypesFilter.forEach(filter => {
        //         store.toggleNewsTypeFilter(filter.key);
        //     });
        // }

        const pressAreaFiltersUnselected = store.selectedNewsTypes.length === 0 && props.pressArea;

        const payload = {
            keywords: escapeQuery(query),
            currentPage: page,
            pageSize: props.sizeOfElements,
            filters: pressAreaFiltersUnselected
                ? store.getNewsFilters(props.pressAreaFilterElements)
                : store.getNewsFilters(),
            locale: unref(cldrLocale),
            searchGroupIds: [window.Liferay?.ThemeDisplay?.getSiteGroupId?.() || null],
        };

        if (store.selectedNewsYears !== null && store.selectedNewsYears.length > 0) {
            if (store.selectedNewsYears.includes('archive')) {
                payload.archive = true;
            } else {
                payload.years = store.selectedNewsYears;
            }
        }

        // Post request to the search interface
        axios
            .post(API_ENDPOINT, payload, {
                responseType: 'json',
                signal: abortController.value.signal,
            })
            .then(response => {
                log?.('🕵️‍ Search response:', response);
                handleResult(response.data);
            })
            .catch(e => error?.(e))
            .finally(() => resetRequest());
    };
    onMounted(() => handleSearch());
    onMounted(() => {
        store.initializeObserver();
    });
    $bus.on('search:news:filter-updated', () => {
        handleSearch();
        store.resetObserver();
    });
    $bus.on('search:news:reached-the-end', () => {
        if (!props.stopLoading) {
            handleSearch(null, store.nextPage);
        }
    });

    const { searchQuery, theEndRef } = storeToRefs(store);
    watchDebounced(
        searchQuery,
        query => {
            log?.(store.searchQuery, query);
            handleSearch(query);
        },
        {
            debounce: SEARCH.SEARCH_WAIT_TIMEOUT,
            maxWait: 5000,
        }
    );

    const swiperRef = ref(null);
    const swiperPreviousRef = ref(null);
    const swiperNextRef = ref(null);
    const handleSwiperInit = swiper => {
        if (!swiper) {
            return;
        }

        if (
            (swiper.currentBreakpoint === '768' && swiper.slides.length < 2.5) ||
            (swiper.currentBreakpoint === '640' && swiper.slides.length < 1.5) ||
            (swiper.currentBreakpoint === 'max' && swiper.slides.length <= 1)
        ) {
            if (swiperNextRef.value) {
                swiperNextRef.value.classList.add('is-hidden');
            }

            if (swiperPreviousRef.value) {
                swiperPreviousRef.value.classList.add('is-hidden');
            }

            if (swiperRef.value && swiperRef.value.classList) {
                swiperRef.value.classList.remove('has-more-elements');
            }
        } else {
            if (swiperNextRef.value) {
                swiperNextRef.value.classList.remove('is-hidden');
            }

            if (swiperPreviousRef.value) {
                swiperPreviousRef.value.classList.remove('is-hidden');
            }

            if (swiperRef.value && swiperRef.value.classList) {
                swiperRef.value.classList.add('has-more-elements');
            }
        }
    };
    const handleSwiperChange = swiper => {
        if (swiperPreviousRef.value) {
            swiperPreviousRef.value.disabled = !!swiper.isBeginning;
        }
        if (swiperNextRef.value) {
            swiperNextRef.value.disabled = !!swiper.isEnd;
        }
    };
    const handleSwiperPrev = () => {
        if (swiperRef.value && swiperRef.value.swiper) {
            swiperRef.value.swiper.slidePrev();
            nextTick(() => {
                handleSwiperChange(swiperRef.value?.swiper);
            });
        }
    };
    const handleSwiperNext = () => {
        if (swiperRef.value && swiperRef.value.swiper) {
            swiperRef.value.swiper.slideNext();
            nextTick(() => {
                handleSwiperChange(swiperRef.value.swiper);
            });
        }
    };

    const handleResetSearch = () => {
        store.searchQuery = null;
    };

    const scroll = (toTheRight = true, className = '') => {
        let scrollContent = document.getElementsByClassName(className)[0];
        if (scrollContent !== null && scrollContent !== undefined) {
            if (toTheRight) {
                scrollContent.scrollLeft += 100;
            } else {
                scrollContent.scrollLeft -= 100;
            }
        }
    };
</script>

<template>
    <section class="news-hub">
        <header class="news-hub-header" v-if="showNewsHeader">
            <div class="container">
                <div class="column">
                    <h1 class="title is-1 news-hub-title">
                        {{ t('news.title') }}
                    </h1>
                    <p class="news-hub-subtitle">
                        {{ t('news.headline') }}
                    </p>
                </div>
            </div>
        </header>

        <div class="news-hub-body" :class="showNewsHeader ? 'header-visible' : 'first-element'">
            <div v-if="showFeaturedNews && store.featuredResults?.length" class="news-hub-featured">
                <div class="container">
                    <h2 class="title is-2 highlight-title" v-if="showHighlightTitle">
                        {{ t('news.highlight.title') }}
                    </h2>
                    <swiper-container
                        ref="swiperRef"
                        slides-per-view="1"
                        space-between="16"
                        @swiperafterinit="handleSwiperInit"
                        class="news-hub-featured-swiper-container"
                        :autoplay="
                            props.enableRotate
                                ? {
                                      delay: (parseFloat(props.autoPlayDelay) * 1000).toString(),
                                      disableOnInteraction: true,
                                  }
                                : undefined
                        "
                        :loop="true"
                    >
                        <swiper-slide
                            class="news-hub-featured-swiper-slide"
                            v-for="(news, index) in store.featuredResults"
                            :key="`featured-${index}`"
                        >
                            <NewsCard :data="news" :fallback-image="fallbackImage" />
                        </swiper-slide>
                    </swiper-container>
                    <footer class="swiper-footer" v-if="store.featuredResults?.length > 1">
                        <div class="swiper-navigation" role="navigation">
                            <button
                                ref="swiperPreviousRef"
                                :aria-label="t('prodTableFooter_previous')"
                                :title="t('prodTableFooter_previous')"
                                disabled="disabled"
                                @click.prevent="handleSwiperPrev"
                            >
                                <svg width="20px" height="20px" aria-hidden="true">
                                    <use xlink:href="#icon-arrow-back"></use>
                                </svg>
                            </button>
                            <button
                                ref="swiperNextRef"
                                :aria-label="t('prodTableFooter_next')"
                                title="t('prodTableFooter_next')"
                                @click.prevent="handleSwiperNext"
                            >
                                <svg width="20px" height="20px" aria-hidden="true">
                                    <use xlink:href="#icon-arrow-forward"></use>
                                </svg>
                            </button>
                        </div>
                    </footer>
                </div>
            </div>
        </div>

        <aside
            class="news-hub-questions"
            v-if="showSearchBar || showTypeFilter || showCategoryFilter || showYearFilter"
        >
            <div class="container">
                <div v-if="showSearchBar" class="news-hub-search-container">
                    <label class="news-hub-search">
                        <span class="icon" :aria-label="t('news.search.label')">
                            <svg class="icon-search" aria-hidden="true">
                                <use xlink:href="#icon-search"></use>
                            </svg>
                        </span>
                        <input v-model="searchQuery" type="search" :placeholder="t('news.search.placeholder')" />
                        <button
                            class="news-hub-search-reset"
                            aria-label="Reset search"
                            @click.prevent="() => handleResetSearch()"
                        >
                            <span class="icon">
                                <svg width="14px" height="14px">
                                    <use xlink:href="#icon-close"></use>
                                </svg>
                            </span>
                        </button>
                    </label>
                </div>
                <div class="news-hub-question" v-if="showTypeFilter">
                    <strong class="question">
                        {{ t('news.question.type') }}
                    </strong>
                    <div class="news-hub-question-swiper-navigation" role="navigation">
                        <button
                            v-if="!isNewsTypFilterStartVisible"
                            class="prev"
                            aria-label="Previous"
                            @click="scroll(false, 'news-types-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-back"></use>
                            </svg>
                        </button>
                        <button
                            v-if="!isNewsTypFilterEndVisible"
                            class="next"
                            aria-label="Next"
                            @click="scroll(true, 'news-types-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-forward"></use>
                            </svg>
                        </button>
                    </div>
                    <nav class="answers news-types-answers">
                        <span ref="newsTypFilterStartRef" class="is-hidden-desktop" />
                        <button
                            v-for="type in newsTypes"
                            :key="type.key"
                            class="button"
                            :class="{
                                'is-primary': store.selectedNewsTypes.includes(type.key),
                                'is-secondary is-outlined': !store.selectedNewsTypes.includes(type.key),
                            }"
                            :disabled="store.busy"
                            @click.prevent="() => store.toggleNewsTypeFilter(type.key)"
                        >
                            {{ type.title }}
                        </button>
                        <span ref="newsTypFilterEndRef" class="is-hidden-desktop" />
                    </nav>
                    <button
                        class="action icon"
                        :aria-label="t('newsHub_resetFilter')"
                        :title="t('newsHub_resetFilter')"
                        :disabled="store.busy || !store.selectedNewsTypes.length"
                        @click.prevent="() => store.resetNewsTypeFilter()"
                    >
                        <svg class="icon-remove-circle">
                            <use xlink:href="#remove-circle"></use>
                        </svg>
                    </button>
                </div>

                <div class="news-hub-question" v-if="showCategoryFilter">
                    <strong class="question">
                        {{ t('news.question.category') }}
                    </strong>
                    <div class="news-hub-question-swiper-navigation" role="navigation">
                        <button
                            v-if="!isNewsCategoryFilterStartVisible"
                            class="prev"
                            aria-label="Previous"
                            @click="scroll(false, 'news-categories-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-back"></use>
                            </svg>
                        </button>
                        <button
                            v-if="!isNewsCategoryFilterEndVisible"
                            class="next"
                            aria-label="Next"
                            @click="scroll(true, 'news-categories-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-forward"></use>
                            </svg>
                        </button>
                    </div>
                    <nav class="answers news-categories-answers">
                        <span ref="newsCategoryFilterStartRef" class="is-hidden-desktop" />
                        <div class="category-container">
                            <div class="news-categories-answers-categories">
                                <button
                                    v-for="category in categories"
                                    :key="category.key"
                                    class="button"
                                    :class="{
                                        'is-primary': store.selectedNewsCategories.includes(category.key),
                                        'is-secondary is-outlined': !store.selectedNewsCategories.includes(
                                            category.key
                                        ),
                                    }"
                                    :disabled="store.busy"
                                    @click.prevent="() => store.toggleNewsCategoryFilter(category.key)"
                                >
                                    {{ category.title }}
                                </button>
                            </div>
                            <div class="news-categories-answers-applications">
                                <button
                                    v-for="category in applications"
                                    :key="category.key"
                                    class="button"
                                    :class="{
                                        'is-primary': store.selectedNewsApplications.includes(category.key),
                                        'is-secondary is-outlined': !store.selectedNewsApplications.includes(
                                            category.key
                                        ),
                                    }"
                                    :disabled="store.busy"
                                    @click.prevent="() => store.toggleNewsApplicationFilter(category.key)"
                                >
                                    {{ category.title }}
                                </button>
                            </div>
                        </div>
                        <span ref="newsCategoryFilterEndRef" class="is-hidden-desktop" />
                    </nav>
                    <button
                        class="action icon"
                        :aria-label="t('newsHub_resetFilter')"
                        :title="t('newsHub_resetFilter')"
                        :disabled="
                            store.busy ||
                            !(store.selectedNewsCategories.length || store.selectedNewsApplications.length)
                        "
                        @click.prevent="() => store.resetNewsCategoryAndApplicationFilters()"
                    >
                        <svg class="icon-remove-circle">
                            <use xlink:href="#remove-circle"></use>
                        </svg>
                    </button>
                </div>

                <div class="news-hub-question" v-if="showYearFilter">
                    <strong class="question">
                        {{ t('news.question.year') }}
                    </strong>
                    <div class="news-hub-question-swiper-navigation" role="navigation">
                        <button
                            v-if="!isNewsYearsFilterStartVisible"
                            class="prev"
                            aria-label="Previous"
                            @click="scroll(false, 'news-years-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-back"></use>
                            </svg>
                        </button>
                        <button
                            v-if="!isNewsYearsFilterEndVisible"
                            class="next"
                            aria-label="Next"
                            @click="scroll(true, 'news-years-answers')"
                        >
                            <svg>
                                <use xlink:href="#icon-arrow-forward"></use>
                            </svg>
                        </button>
                    </div>
                    <nav class="answers news-years-answers">
                        <span ref="newsYearsFilterStartRef" class="is-hidden-desktop" />
                        <button
                            v-for="year in years"
                            :key="year"
                            class="button"
                            :class="{
                                'is-primary': store.selectedNewsYears.includes(year),
                                'is-secondary is-outlined': !store.selectedNewsYears.includes(year),
                            }"
                            :disabled="store.busy"
                            @click.prevent="() => store.toggleNewsYearFilter(year)"
                        >
                            {{ year }}
                        </button>
                        <span class="separator" aria-hidden="true">/</span>
                        <button
                            class="button"
                            :class="{
                                'is-primary': store.selectedNewsYears.includes(SEARCH.NEWS_YEAR_ARCHIVE),
                                'is-secondary is-outlined': !store.selectedNewsYears.includes(SEARCH.NEWS_YEAR_ARCHIVE),
                            }"
                            :disabled="store.busy"
                            @click.prevent="() => store.toggleNewsYearFilter(SEARCH.NEWS_YEAR_ARCHIVE)"
                        >
                            {{ t('newsHub_archive') }}
                        </button>
                        <span ref="newsYearsFilterEndRef" class="is-hidden-desktop" />
                    </nav>
                    <button
                        class="action icon"
                        :aria-label="t('newsHub_resetFilter')"
                        :title="t('newsHub_resetFilter')"
                        :disabled="store.busy || store.selectedNewsYears.length === 0"
                        @click.prevent="store.resetNewsYearFilter()"
                    >
                        <svg class="icon-remove-circle" aria-hidden="true">
                            <use xlink:href="#remove-circle"></use>
                        </svg>
                    </button>
                </div>
            </div>
        </aside>

        <template v-if="showPressLinks">
            <PressLinks
                :contact-person-label="contactPersonLabel"
                :contact-person-url="contactPersonUrl"
                :multimedia-label="multimediaLabel"
                :multimedia-url="multimediaUrl"
                :newsletter-label="newsletterLabel"
                :newsletter-url="newsletterUrl"
            />
        </template>

        <div ref="newsListRef" class="news-hub-body">
            <div class="news-hub-news">
                <div class="container">
                    <NewsTeaserCard
                        v-if="showNewsTeaser"
                        :title="newsTeaserTitle"
                        :abstract="newsTeaserAbstract"
                        :link-href="newsTeaserLinkHref"
                        :button-label="newsTeaserLinkText"
                        :background-image="newsTeaserBackgroundImage"
                    />
                    <NewsCard v-for="(news, index) in store.results" :key="`news-${index}`" :data="news" />
                    <SkeletonLoader v-if="store.busy" />
                    <SkeletonLoader v-if="store.busy" />
                </div>
            </div>

            <div v-if="!store.busy && !store.results.length && !store.featuredResults.length" class="has-text-centered">
                There are no news in this category.
            </div>
        </div>

        <div ref="theEndRef" class="the-end">
            <Spinner v-if="store.busy" class="search-spinner" :size="'large'" :line-fg-color="'#46555f'" />
        </div>
    </section>
</template>

<style lang="scss">
    @import '../../../scss/utilities/all';
    @import '../../../scss/elements/title';

    $clickable-card-hover-box-shadow: 0 0.5rem 1.5rem -0.5rem rgb(0 0 0 / 28%) !default;

    .news-hub {
        &-header,
        &-questions,
        &-body {
            padding-left: 1rem;
            padding-right: 1rem;
        }

        &-header {
            position: relative;
            padding-top: 3rem;
            padding-bottom: 3rem;
            background-image: url(https://look.ams-osram.com/transform/98089e6c-3697-4e87-b85a-a3e42ad9899d/ams-OSRAM-Texture-Assets?format=webp&io=transform:fill,width:960);
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center center;
            color: var(--color-text-inverted);

            @include desktop {
                background-image: url(https://look.ams-osram.com/transform/98089e6c-3697-4e87-b85a-a3e42ad9899d/ams-OSRAM-Texture-Assets?format=webp&io=transform:fill,width:2160);
            }

            &:after {
                display: block;
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: linear-gradient(
                    180deg,
                    hsl(var(--colors-dark-gray-500) / 0) 0%,
                    hsl(var(--colors-dark-gray-500), 1) 100%
                );
                pointer-events: none;
            }

            .container {
                position: relative;
                z-index: 1;
                display: grid;
                grid-template-columns: 1fr;
                grid-template-rows: auto;
                grid-gap: 2rem;

                @include tablet {
                    grid-template-columns: minmax(auto, 35rem) auto;
                }
            }
        }

        &-title {
            color: var(--color-text-inverted);
        }

        &-search {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0 1rem;
            width: 100%;
            border: 1px solid hsl(var(--colors-base-white));
            border-radius: var(--radius);
            backdrop-filter: blur(0.5rem);
            transition: var(--sub-nav-speed) all ease-out;
            background-color: #e0d1c4;

            input {
                flex: 1 1 auto;
                appearance: none;
                border: 0 none;
                background: transparent;
                color: var(--color-text-secondary);
                padding: 1rem;
            }

            > .icon {
                color: var(--color-text-secondary);
                transition: var(--sub-nav-speed) color ease-out;

                &,
                svg {
                    width: 1.25rem;
                    height: 1.25rem;
                }
            }

            &-reset {
                flex: 0 0 auto;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                appearance: none;
                padding: 0;
                border: 0 none;
                background: transparent;
                color: var(--color-text-secondary);
                transition: var(--sub-nav-speed) color ease-out;
                cursor: pointer;

                .icon,
                svg {
                    width: 0.875rem;
                    height: 0.875rem;
                }

                &:hover {
                    color: var(--color-text-inverted);
                }
            }
        }

        &-questions {
            margin-bottom: 3rem;
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
            background-color: var(--color-bg-cream);

            .container {
                display: flex;
                flex-direction: column;
                gap: 1.5rem;
            }
        }

        &-question {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            grid-gap: 0.5rem;

            @include tablet {
                grid-template-columns: minmax(auto, 8rem) 1fr 2rem;
                grid-gap: 1.5rem;
            }

            .question {
                display: block;

                @include tablet {
                    line-height: 2.25rem;
                }
            }

            .answers {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 0.5rem;

                @include mobile {
                    margin: 0;
                    padding: 0 2px 0 0;
                    flex-wrap: nowrap;
                    overflow-x: auto;
                    -ms-overflow-style: none;
                    scrollbar-width: none;

                    &::-webkit-scrollbar {
                        display: none;
                    }
                }

                .is-hidden-desktop {
                    display: none;

                    @include mobile {
                        display: block;
                    }
                }

                .separator {
                    display: inline-flex;
                    flex: 0 0 auto;
                    margin: 0;
                    border: 0 none;
                }

                .button {
                    @include mobile {
                        white-space: nowrap;
                        flex: 0 0 auto;
                        width: auto;
                        font-size: 0.875rem;
                    }
                }

                .category-container {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .news-categories-answers-categories,
                .news-categories-answers-applications {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 0.5rem;

                    @include mobile {
                        flex-wrap: nowrap;
                    }
                }
            }

            .action {
                appearance: none;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 1.25rem;
                height: 2.25rem;
                padding: 0;
                border: 0 none;
                background: transparent;
                color: var(--color-text-primary);
                cursor: pointer;
                transition: var(--sub-nav-speed) color ease-out;

                &:disabled {
                    cursor: default;
                    color: hsl(var(--colors-secondary-500) / 0.5);
                }

                @include mobile {
                    display: none !important;
                }

                &:not(:disabled):hover {
                    color: hsl(var(--colors-secondary-500) / 1);
                }

                svg {
                    width: 1.25rem;
                    height: 1.25rem;
                }
            }
        }

        &-featured,
        &-news {
            margin-bottom: 5rem;
        }

        &-featured {
            &-swiper-container {
                height: 21.1rem;

                @include mobile {
                    height: fit-content;
                }

                .news-card {
                    flex-direction: row !important;
                    width: calc(100% - 0.5rem);

                    .news-card-image {
                        width: 50%;

                        @include mobile {
                            width: 100%;
                        }
                    }

                    @include mobile {
                        flex-direction: column !important;
                    }
                }
            }

            &-swiper-slide {
                padding-bottom: 1rem;
            }
        }

        &-news {
            .container {
                display: grid;
                grid-template-columns: 1fr;
                grid-template-rows: auto;
                grid-gap: 1rem;

                @include tablet {
                    grid-template-columns: repeat(2, 1fr);
                }

                @include desktop {
                    grid-template-columns: repeat(3, 1fr);
                }

                .list-enter-active,
                .list-leave-active {
                    transition: opacity 0.4s ease-out;
                }
                .list-enter-from,
                .list-leave-to {
                    opacity: 0;
                }
            }
        }

        &-body {
            &.header-visible {
                margin-top: 5rem;
            }
        }

        .news-card {
            display: flex;
            flex-direction: column;
            height: 100%;
            text-decoration: none;
            background-color: var(--color-text-inverted);
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center center;
            color: var(--color-text);
            box-shadow: none;
            transition: var(--sub-nav-speed) all ease-out;
            will-change: box-shadow, transform;

            &.is-featured {
                position: relative;
                aspect-ratio: 524/500;
                height: 20rem;
                background-color: var(--color-bg-secondary);

                &:after {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 1;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(
                        180deg,
                        hsl(var(--colors-base-black) / 0) 0%,
                        hsl(var(--colors-base-black) / 0.9) 70%
                    );
                    pointer-events: none;
                }

                .news-card-title {
                    flex: 0 0 auto;
                    margin-top: auto;
                    color: var(--color-text-inverted);
                }

                .news-card-meta {
                    color: hsl(var(--colors-base-white) / 0.6);
                }
            }

            &-image {
                aspect-ratio: 16/10;
                object-fit: cover;
                object-position: center center;

                &.is-placeholder {
                    background-color: hsl(var(--colors-secondary-500) / 0.2);

                    &.is-loading {
                        position: relative;
                        overflow: hidden;

                        &::after {
                            position: absolute;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            left: 0;
                            transform: translateX(-100%);
                            background-image: linear-gradient(
                                90deg,
                                rgba(#fff, 0) 0,
                                rgba(#fff, 0.1) 20%,
                                rgba(#fff, 0.4) 60%,
                                rgba(#fff, 0)
                            );
                            animation: shimmer 2s infinite;
                            animation-delay: 1s;
                            content: '';
                        }
                    }
                }
            }

            &-title,
            &-meta,
            &-actions {
                position: relative;
                z-index: 2;

                &.is-placeholder {
                    position: relative;
                    overflow: hidden;

                    &:before {
                        content: '';
                        display: inline-block;
                        height: 1rem;
                        border-radius: var(--radius-small);
                        background-color: hsl(var(--colors-secondary-500) / 0.2);
                    }

                    &.is-loading {
                        position: relative;
                        overflow: hidden;

                        &::after {
                            position: absolute;
                            top: 0;
                            right: 0;
                            bottom: 0;
                            left: 0;
                            transform: translateX(-100%);
                            background-image: linear-gradient(
                                90deg,
                                rgba(#fff, 0) 0,
                                rgba(#fff, 0.1) 20%,
                                rgba(#fff, 0.4) 60%,
                                rgba(#fff, 0)
                            );
                            animation: shimmer 2s infinite;
                            animation-delay: 1s;
                            content: '';
                        }
                    }
                }
            }

            &-title {
                flex: 1 1 auto;
                margin: 0 0 3rem;
                padding: 1.5rem 1rem 0.15rem;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 4;
                overflow: hidden;
                height: calc(25.2px * 4);

                &.is-placeholder:before {
                    width: 60%;
                    height: 1.5rem;
                }
            }

            &-meta {
                padding: 0 1rem 1.5rem;
                color: hsl(var(--colors-secondary-500) / 0.5);
                font-weight: var(--weight-normal);

                &.is-placeholder:before {
                    width: 80%;
                }

                &-time {
                    display: block;
                }
            }

            &-actions {
                padding: 0 1rem 1rem;

                &.is-placeholder:before {
                    width: 28%;
                    height: 2.125rem;
                }
            }

            &:not(.is-skeleton):hover {
                // transform: scale(1.025);
                box-shadow: $clickable-card-hover-box-shadow;

                .button {
                    background-color: var(--color-bg-primary);
                    border-color: var(--color-border-primary);
                    color: var(--color-text-inverted);
                }
            }

            &-info-container {
                background-color: var(--color-bg);
                background-position: 50%;
                background-repeat: no-repeat;
                background-size: cover;
                box-shadow: none;
                color: var(--color-text);
                display: flex;
                flex-direction: column;
                height: 100%;
                text-decoration: none;
                transition: all 0.06s ease-out;
                will-change: box-shadow, transform;
            }
        }

        swiper-container {
            &.has-more-elements {
                &:after {
                    @include tablet {
                        content: '';
                        position: absolute;
                        width: 19%;
                        height: 100%;
                        top: 0;
                        right: 0;
                        pointer-events: none;
                        background-color: transparent;
                        z-index: 1;
                    }

                    @include desktop {
                        background-color: transparent;
                    }
                }
            }
        }

        .swiper-footer {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            padding-top: 1.5rem;

            button {
                border: 0 none;
                background: transparent;

                &:not(:disabled) {
                    cursor: pointer;
                }
            }
        }

        .the-end {
            margin-bottom: 5rem;
            text-align: center;
        }
    }

    .news-hub-question-swiper-navigation {
        margin: -2rem;
        position: absolute;
        margin-top: 1.75rem;
        width: 100vw;

        @include desktop {
            display: none;
        }

        @include tablet-only {
            display: none;
        }

        button {
            border: none;
            padding: 0;
            width: 2.5rem;
            height: 2.25rem;
            z-index: 200;
            position: absolute;

            &.prev {
                background: linear-gradient(90deg, var(--color-bg-cream) 70%, transparent 100%);
            }

            &.next {
                background: linear-gradient(270deg, var(--color-bg-cream) 70%, transparent 100%);
                right: 0;
            }

            svg {
                width: 1rem;
                height: 1rem;
                margin-top: -0.25rem;
            }
        }
    }

    @keyframes shimmer {
        100% {
            transform: translateX(100%);
        }
    }
</style>
